import { z } from 'zod';
import { BusinessCodeSchema, NoteSchema, RecordIdSchema } from './identifiers';
import { LocalizedLabelSchema } from './localized';
import {
  CurrencyCodeSchema,
  DecimalStringSchema,
  MoneySchema,
  addMoney,
  allocateMoney,
  compareMoney,
  isNegativeMoney,
  isZeroMoney,
  money,
  subtractMoney,
  type Money,
} from './money';
import { HistoricalOriginSchema } from './origin';
import { BusinessDateSchema, InstantSchema, addMonths, type BusinessDate } from './time';

/**
 * Sales — `SALE-*` demonstration slice (ADR-0025).
 *
 * Reservation → contract → installment schedule. Two invariants run through all of it:
 *
 * - **A unit is committed exactly once.** Every state change that touches a unit happens inside the
 *   same transaction as the record that caused it, so there is no window in which a unit is reserved
 *   by a reservation that does not exist, or free while a contract points at it.
 * - **A schedule reconciles exactly.** The down payment plus every installment plus any final payment
 *   sum to the contract total, to the last piastre, for every input — including totals that do not
 *   divide evenly. This is checked when the schedule is built and again before it is stored.
 */

/* ---------------------------------------------------------------- payment plan */

export const INSTALLMENT_FREQUENCIES = ['monthly', 'quarterly', 'semiAnnual', 'annual'] as const;
export const InstallmentFrequencySchema = z.enum(INSTALLMENT_FREQUENCIES);
export type InstallmentFrequency = z.infer<typeof InstallmentFrequencySchema>;

export const MONTHS_PER_FREQUENCY: Readonly<Record<InstallmentFrequency, number>> = {
  monthly: 1,
  quarterly: 3,
  semiAnnual: 6,
  annual: 12,
};

/** How many installments one plan may carry. A demo bound, not a business rule. */
export const MAX_INSTALLMENTS = 240;

/** How many dated milestone rows one plan may carry. A bound, not a business rule. */
export const MAX_MILESTONES = 24;

/**
 * A payment tied to a date the parties agree — handover, completion of the structure — rather than to
 * the periodic cycle (COL-SCHEDULE-001). Part of the price: the periodic instalments split what is left
 * after the down payment, the final payment and every milestone.
 */
export const PlanMilestoneSchema = z.strictObject({
  dueOn: BusinessDateSchema,
  amount: MoneySchema,
  /** What the payment is tied to, in both languages (I18N-009). */
  label: LocalizedLabelSchema.optional(),
});
export type PlanMilestone = z.infer<typeof PlanMilestoneSchema>;

/**
 * Money held for the building's upkeep. It is **added to** the price, never taken from it, and
 * appears as its own row. There is no default amount: until `BD-32` decides one, a plan carries a
 * maintenance deposit only when the person entering it states one.
 */
export const MaintenanceDepositSchema = z.strictObject({
  amount: MoneySchema,
  dueOn: BusinessDateSchema,
});
export type MaintenanceDeposit = z.infer<typeof MaintenanceDepositSchema>;

/**
 * The rounding rule every schedule uses (`BD-32`, proposed): an amount that does not divide evenly
 * gives its odd piastres to the earliest instalments, one each. Stated on every preview so a person
 * approving a schedule sees the rule rather than trusting it.
 */
export const SCHEDULE_ROUNDING_RULE = 'oddPiastresToEarliestRows' as const;

export const PaymentPlanSchema = z.strictObject({
  /** Paid at signing. May be zero. */
  downPayment: MoneySchema,
  installmentCount: z.number().int().min(0).max(MAX_INSTALLMENTS),
  frequency: InstallmentFrequencySchema,
  /**
   * The date installment **number one** falls due — not an origin one period before it. Installment
   * *k* is due `firstDueOn + (k − 1) × frequency`, so a monthly plan starting 1 October has its first
   * installment on 1 October.
   */
  firstDueOn: BusinessDateSchema,
  /**
   * When the down payment is due. Omitted means the same day the installments start.
   *
   * Separate from `firstDueOn` because the common arrangement is a deposit at signing and the first
   * installment a period later, and collapsing the two would date the deposit a month after the
   * customer actually paid it.
   */
  downPaymentDueOn: BusinessDateSchema.optional(),
  /** A balloon payment after the last installment. May be omitted. */
  finalPayment: MoneySchema.optional(),
  /** Dated rows tied to milestones, part of the price (COL-SCHEDULE-001). */
  milestones: z.array(PlanMilestoneSchema).max(MAX_MILESTONES).optional(),
  /** Added to the price as its own row (`BD-32`). */
  maintenanceDeposit: MaintenanceDepositSchema.optional(),
});
export type PaymentPlan = z.infer<typeof PaymentPlanSchema>;

export const INSTALLMENT_KINDS = [
  'downPayment',
  'installment',
  'finalPayment',
  'milestone',
  'maintenanceDeposit',
] as const;
export const InstallmentKindSchema = z.enum(INSTALLMENT_KINDS);
export type InstallmentKind = z.infer<typeof InstallmentKindSchema>;

/** One row of a generated schedule, before it becomes a stored installment. */
export const ScheduleRowSchema = z.strictObject({
  sequence: z.number().int().positive(),
  kind: InstallmentKindSchema,
  dueOn: BusinessDateSchema,
  amount: MoneySchema,
  /** A milestone's wording. */
  label: LocalizedLabelSchema.optional(),
});
export type ScheduleRow = z.infer<typeof ScheduleRowSchema>;

export class PaymentPlanError extends Error {
  readonly code = 'VALIDATION_FAILED';
  constructor(
    readonly issue:
      | 'CURRENCY_MISMATCH'
      | 'NEGATIVE_AMOUNT'
      | 'DOWN_PAYMENT_EXCEEDS_TOTAL'
      | 'FINAL_PAYMENT_EXCEEDS_REMAINDER'
      | 'MILESTONES_EXCEED_REMAINDER'
      | 'EMPTY_ROW'
      | 'NO_INSTALLMENTS_FOR_REMAINDER'
      | 'SCHEDULE_DOES_NOT_RECONCILE',
  ) {
    super(issue);
    this.name = 'PaymentPlanError';
  }
}

/**
 * Build the installment schedule for a total and a plan.
 *
 * Pure, so the preview a person approves on screen is produced by exactly the code that stores the
 * schedule — not by a second implementation that can disagree with it.
 *
 * The remainder after the down payment and any final payment is split with `allocateMoney`, which
 * gives the earliest rows the odd piastres one at a time. That is deterministic, auditable, and it is
 * why 1,000,000 over 3 installments produces 333,333.34 / 333,333.33 / 333,333.33 rather than three
 * rows that quietly lose a piastre.
 */
export function buildInstallmentSchedule(total: Money, plan: PaymentPlan): ScheduleRow[] {
  const currency = total.currency;
  const milestones = plan.milestones ?? [];
  const extras: Money[] = [
    plan.downPayment,
    ...(plan.finalPayment ? [plan.finalPayment] : []),
    ...milestones.map((milestone) => milestone.amount),
    ...(plan.maintenanceDeposit ? [plan.maintenanceDeposit.amount] : []),
  ];
  if (extras.some((amount) => amount.currency !== currency)) {
    throw new PaymentPlanError('CURRENCY_MISMATCH');
  }
  if (isNegativeMoney(total) || extras.some((amount) => isNegativeMoney(amount))) {
    throw new PaymentPlanError('NEGATIVE_AMOUNT');
  }
  // A milestone or maintenance row of nothing is a row nobody can pay; refuse it rather than store it.
  if (
    milestones.some((milestone) => isZeroMoney(milestone.amount)) ||
    (plan.maintenanceDeposit && isZeroMoney(plan.maintenanceDeposit.amount))
  ) {
    throw new PaymentPlanError('EMPTY_ROW');
  }
  if (compareMoney(plan.downPayment, total) > 0) {
    throw new PaymentPlanError('DOWN_PAYMENT_EXCEEDS_TOTAL');
  }

  const zero = money('0', currency);
  const afterDownPayment = subtractMoney(total, plan.downPayment);
  const finalPayment = plan.finalPayment ?? zero;
  if (compareMoney(finalPayment, afterDownPayment) > 0) {
    throw new PaymentPlanError('FINAL_PAYMENT_EXCEEDS_REMAINDER');
  }
  const milestoneTotal = milestones.reduce<Money>(
    (running, milestone) => addMoney(running, milestone.amount),
    zero,
  );
  const afterFinal = subtractMoney(afterDownPayment, finalPayment);
  if (compareMoney(milestoneTotal, afterFinal) > 0) {
    throw new PaymentPlanError('MILESTONES_EXCEED_REMAINDER');
  }
  const installmentTotal = subtractMoney(afterFinal, milestoneTotal);

  // Money left to spread and nowhere to spread it: refuse rather than silently drop it.
  if (plan.installmentCount === 0 && compareMoney(installmentTotal, zero) !== 0) {
    throw new PaymentPlanError('NO_INSTALLMENTS_FOR_REMAINDER');
  }

  const rows: Omit<ScheduleRow, 'sequence'>[] = [];

  if (compareMoney(plan.downPayment, zero) !== 0) {
    rows.push({
      kind: 'downPayment',
      // Due at signing when the caller says so; otherwise the day the installments start.
      dueOn: plan.downPaymentDueOn ?? plan.firstDueOn,
      amount: plan.downPayment,
    });
  }

  if (plan.installmentCount > 0) {
    const parts = allocateMoney(
      installmentTotal,
      Array.from({ length: plan.installmentCount }, () => 1),
      2,
    );
    const step = MONTHS_PER_FREQUENCY[plan.frequency];
    for (let index = 0; index < plan.installmentCount; index += 1) {
      rows.push({
        kind: 'installment',
        // Installment 1 falls on firstDueOn itself. Every date is computed from that origin rather
        // than from the previous row, so a clamped month can never shift the whole tail.
        dueOn: addMonths(plan.firstDueOn, step * index),
        amount: parts[index] as Money,
      });
    }
  }

  for (const milestone of milestones) {
    rows.push({
      kind: 'milestone',
      dueOn: milestone.dueOn,
      amount: milestone.amount,
      ...(milestone.label ? { label: milestone.label } : {}),
    });
  }

  if (compareMoney(finalPayment, zero) !== 0) {
    const step = MONTHS_PER_FREQUENCY[plan.frequency];
    rows.push({
      kind: 'finalPayment',
      // One period after the last installment.
      dueOn: addMonths(plan.firstDueOn, step * plan.installmentCount),
      amount: finalPayment,
    });
  }

  if (plan.maintenanceDeposit) {
    rows.push({
      kind: 'maintenanceDeposit',
      dueOn: plan.maintenanceDeposit.dueOn,
      amount: plan.maintenanceDeposit.amount,
    });
  }

  // Rows in date order, numbered from one. The sort is stable, so rows due the same day keep the
  // order above — a plan without milestones numbers exactly as it always did.
  const ordered = rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) =>
      a.row.dueOn === b.row.dueOn ? a.index - b.index : a.row.dueOn < b.row.dueOn ? -1 : 1,
    )
    .map(({ row }, index): ScheduleRow => ({ sequence: index + 1, ...row }));

  // The reconciliation is asserted here, not assumed. A schedule that does not add up must never be
  // stored: the arithmetic error would surface months later as a balance nobody can explain.
  const sum = ordered.reduce<Money>((running, row) => addMoney(running, row.amount), zero);
  if (compareMoney(sum, scheduleTotal(total, plan)) !== 0) {
    throw new PaymentPlanError('SCHEDULE_DOES_NOT_RECONCILE');
  }
  return ordered;
}

/**
 * What a schedule adds up to: the price plus any maintenance deposit. This is the contract total —
 * paid plus outstanding always equals it.
 */
export function scheduleTotal(price: Money, plan: PaymentPlan): Money {
  return plan.maintenanceDeposit ? addMoney(price, plan.maintenanceDeposit.amount) : price;
}

/* ----------------------------------------------------------------- reservation */

/**
 * SALE-RESERVE-001. `approved` means every approval the reservation needed was granted and it waits
 * for a person to confirm it; `rejected` means one was refused, and the unit is back on sale. Both
 * were added in BMP-1 after the demonstration's states, so stored records keep their meaning.
 */
export const RESERVATION_STATES = [
  'draft',
  'pendingApproval',
  'confirmed',
  'cancelled',
  'expired',
  'converted',
  'approved',
  'rejected',
] as const;
export const ReservationStateSchema = z.enum(RESERVATION_STATES);
export type ReservationState = z.infer<typeof ReservationStateSchema>;

export const RESERVATION_TRANSITIONS: Readonly<
  Record<ReservationState, readonly ReservationState[]>
> = {
  draft: ['pendingApproval', 'confirmed', 'cancelled', 'expired'],
  // `confirmed` directly from `pendingApproval` remains for a record whose approval was granted
  // before the `approved` state existed; the service still requires the approval to be granted.
  pendingApproval: ['approved', 'rejected', 'confirmed', 'cancelled', 'expired'],
  approved: ['confirmed', 'cancelled', 'expired'],
  confirmed: ['converted', 'cancelled', 'expired'],
  cancelled: [],
  expired: [],
  converted: [],
  rejected: [],
};

/** A reservation that still holds its unit. */
export const LIVE_RESERVATION_STATES: readonly ReservationState[] = [
  'draft',
  'pendingApproval',
  'approved',
  'confirmed',
];

/**
 * The commercial operations sales submits to the approval engine (SALE-DISCOUNT, SALE-RESERVE,
 * SALE-CONTRACT, SALE-CHANGE). Each is governed only when a published policy names it (`BD-04`);
 * the demonstration seed publishes illustrative ones, and a client deployment publishes its own.
 */
export const SALES_APPROVAL_OPERATIONS = {
  discount: 'sales.reservation.discount',
  priceOverride: 'sales.reservation.priceOverride',
  reservationException: 'sales.reservation.exception',
  reservationExtension: 'sales.reservation.extension',
  reservationCancellation: 'sales.reservation.cancellation',
  contractException: 'sales.contract.exception',
  contractAmendment: 'sales.contract.amendment',
  contractCancellation: 'sales.contract.cancellation',
  /** SALE-CHANGE-002 (BD-07): moving an active contract to another unit. */
  unitReplacement: 'sales.contract.unitReplacement',
  /** SALE-CHANGE-003 (BD-08): assigning an active contract to another buyer. */
  assignment: 'sales.contract.assignment',
} as const;
export type SalesApprovalOperation =
  (typeof SALES_APPROVAL_OPERATIONS)[keyof typeof SALES_APPROVAL_OPERATIONS];

/**
 * Why a reservation needs an exception approval. Each is refused outright when no policy can approve
 * it — an exception is never granted by the absence of a control.
 */
export const RESERVATION_EXCEPTIONS = ['discountAboveMaximum', 'depositBelowMinimum'] as const;
export type ReservationException = (typeof RESERVATION_EXCEPTIONS)[number];

/** What the finance side (BMP-2) must do about money taken on a reservation that ended. */
/** `handedOver`: a refund request exists in collections for the money to return (SALE-CANCEL-002). */
export const REFUND_HANDOFF_STATES = ['notApplicable', 'pending', 'handedOver'] as const;

export function canTransitionReservation(from: ReservationState, to: ReservationState): boolean {
  return RESERVATION_TRANSITIONS[from].includes(to);
}

export const ReservationSchema = z.strictObject({
  reservationId: RecordIdSchema,
  /** Human-facing and immutable once issued. */
  reservationNumber: BusinessCodeSchema,
  customerId: RecordIdSchema,
  leadId: RecordIdSchema.optional(),
  /** The opportunity this reservation advanced (CRM-OPP-002). */
  opportunityId: RecordIdSchema.optional(),
  /** The timed hold it was converted from (INV-HOLD-001). */
  holdId: RecordIdSchema.optional(),
  unitId: RecordIdSchema,
  projectId: RecordIdSchema,
  reservedOn: BusinessDateSchema,
  /** The day it was confirmed; absent on one confirmed before this was recorded. */
  confirmedOn: BusinessDateSchema.optional(),
  expiresOn: BusinessDateSchema,
  reservationAmount: MoneySchema,
  /** The price agreed with the customer; may be below the unit's current price. */
  agreedPrice: MoneySchema,
  /** The unit's price when the reservation was made — what the discount is measured against. */
  listPrice: MoneySchema.optional(),
  /** Derived from the unit's current price and the agreed price, as a decimal string fraction. */
  discountPercentage: z.string(),
  /** The deposit the configured rule asked for when the reservation was made (`BD-02`). */
  minimumDeposit: MoneySchema.optional(),
  exceptions: z.array(z.enum(RESERVATION_EXCEPTIONS)),
  /** Every approval request the reservation waits on, by operation. */
  approvals: z.array(
    z.strictObject({ operationType: z.string(), requestId: z.string().min(1).max(200) }),
  ),
  paymentPlan: PaymentPlanSchema,
  salesOwnerAccountId: z.string().min(1).max(200),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  departmentId: RecordIdSchema.optional(),
  teamId: RecordIdSchema.optional(),
  state: ReservationStateSchema,
  /** Set when the discount triggered an approval. The engine owns the outcome (ADR-0024). */
  approvalRequestId: z.string().min(1).max(200).optional(),
  contractId: RecordIdSchema.optional(),
  cancellationReason: z.string().max(500).optional(),
  /**
   * `handedOver` when the reservation ended holding effective deposit receipts: `refundId` names the
   * refund obligation created in the same transaction. `pending` only on records from before
   * 2026-10-21, when the agreed amount was assumed to be money received.
   */
  refundHandoff: z.enum(REFUND_HANDOFF_STATES),
  refundId: RecordIdSchema.optional(),
  /** An extension or a cancellation waiting for approval (SALE-RESERVE-003, 005). */
  pendingExtension: z
    .strictObject({ days: z.number().int().positive(), requestId: z.string(), reason: z.string() })
    .optional(),
  pendingCancellation: z.strictObject({ requestId: z.string(), reason: z.string() }).optional(),
  extensions: z.number().int().nonnegative(),
  notes: NoteSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Reservation = z.infer<typeof ReservationSchema>;

export const CreateReservationSchema = z.strictObject({
  customerId: RecordIdSchema,
  leadId: RecordIdSchema.optional(),
  opportunityId: RecordIdSchema.optional(),
  /** Reserve the unit the actor's timed hold already holds, instead of an available one. */
  holdId: RecordIdSchema.optional(),
  unitId: RecordIdSchema,
  reservationAmount: MoneySchema,
  agreedPrice: MoneySchema,
  paymentPlan: PaymentPlanSchema,
  /**
   * The validity is `sales.reservationValidityDays` (`BD-01`); when given, this must equal it. There
   * is no default: an unconfigured validity refuses the reservation rather than inventing one.
   */
  holdDays: z.number().int().min(1).max(365).optional(),
  notes: NoteSchema.optional(),
  /**
   * Idempotency key. A retried submission returns the original reservation instead of taking a second
   * hold on the unit; a replay carrying different input is a conflict, not a silent no-op.
   */
  idempotencyKey: z.string().min(8).max(200),
});
export type CreateReservation = z.infer<typeof CreateReservationSchema>;

export const CancelReservationSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
});
export type CancelReservation = z.infer<typeof CancelReservationSchema>;

export const ExtendReservationSchema = z.strictObject({
  days: z.number().int().min(1).max(180),
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type ExtendReservation = z.infer<typeof ExtendReservationSchema>;

/**
 * The reservation's descriptive part, edited directly (2026-10-23). Only the notes: the customer, the
 * unit, the price, the deposit, the plan and the dates each change through their own workflow — an
 * approval, a replacement, a deposit, an extension or a cancellation — never through a generic edit.
 */
export const UpdateReservationSchema = z.strictObject({
  notes: NoteSchema,
  expectedVersion: z.number().int().positive(),
});
export type UpdateReservation = z.infer<typeof UpdateReservationSchema>;

/** States in which a reservation's notes may still be edited: it is live and no contract drafts it. */
export const RESERVATION_EDITABLE_STATES = [
  'draft',
  'pendingApproval',
  'approved',
  'confirmed',
] as const satisfies readonly (typeof RESERVATION_STATES)[number][];

export const ReservationQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
  state: ReservationStateSchema.optional(),
  projectId: RecordIdSchema.optional(),
  customerId: RecordIdSchema.optional(),
  unitId: RecordIdSchema.optional(),
  opportunityId: RecordIdSchema.optional(),
  salesOwnerAccountId: z.string().min(1).max(200).optional(),
});
export type ReservationQuery = z.infer<typeof ReservationQuerySchema>;

export const ReservationPageSchema = z.strictObject({
  items: z.array(ReservationSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type ReservationPage = z.infer<typeof ReservationPageSchema>;

/* -------------------------------------------------------------------- contract */

/**
 * SALE-CONTRACT-001, 003. A contract is created as a **draft** that carries snapshots and a proposed
 * schedule; nothing is collectible and the unit is still the reservation's. Activation freezes the
 * schedule into instalments and commits the unit (`pendingApproval` while a contract exception waits
 * on its approval). Contracts made before BMP-1 were created active and keep that state.
 */
export const CONTRACT_STATES = [
  'draft',
  'pendingApproval',
  'active',
  'cancelled',
  'completed',
] as const;
export const ContractStateSchema = z.enum(CONTRACT_STATES);
export type ContractState = z.infer<typeof ContractStateSchema>;

export const CONTRACT_TRANSITIONS: Readonly<Record<ContractState, readonly ContractState[]>> = {
  draft: ['pendingApproval', 'active', 'cancelled'],
  // Back to `draft` when the exception approval is rejected; the draft can then be corrected.
  pendingApproval: ['draft', 'active', 'cancelled'],
  active: ['completed', 'cancelled'],
  cancelled: [],
  completed: [],
};

export function canTransitionContract(from: ContractState, to: ContractState): boolean {
  return CONTRACT_TRANSITIONS[from].includes(to);
}

/** SALE-CONTRACT-002. Buyers and co-buyers own shares; a guarantor or representative owns none. */
export const CONTRACT_PARTY_ROLES = ['buyer', 'coBuyer', 'guarantor', 'representative'] as const;
export const ContractPartyRoleSchema = z.enum(CONTRACT_PARTY_ROLES);
export type ContractPartyRole = z.infer<typeof ContractPartyRoleSchema>;

/** A share of ownership, in percent, to four decimal places: `50`, `33.3333`. */
export const SharePercentSchema = z
  .string()
  .regex(/^(100(\.0{1,4})?|\d{1,2}(\.\d{1,4})?)$/, { message: 'SHARE_PERCENT_EXPECTED' });

export const ContractPartyInputSchema = z.strictObject({
  role: ContractPartyRoleSchema,
  customerId: RecordIdSchema,
  /** Required for a buyer or co-buyer, refused for anyone else. */
  sharePercent: SharePercentSchema.optional(),
});
export type ContractPartyInput = z.infer<typeof ContractPartyInputSchema>;

export const ContractPartySchema = z.strictObject({
  role: ContractPartyRoleSchema,
  customerId: RecordIdSchema,
  sharePercent: SharePercentSchema.optional(),
  /** The name as it stood when the party was added — a snapshot, never re-read. */
  name: z.string().max(200).optional(),
});
export type ContractParty = z.infer<typeof ContractPartySchema>;

/** At most ten parties on one contract. */
export const MAX_CONTRACT_PARTIES = 10;

/**
 * The identity on the customer snapshot. Kept apart from the CRM schema (which imports inventory,
 * which imports this file) and restricted on the contract exactly as on the customer (SEC-029).
 */
export const ContractIdentitySnapshotSchema = z.strictObject({
  type: z.string().min(1).max(40),
  number: z.string().min(1).max(60),
  issuingCountry: z.string().max(3).optional(),
});

/** The buyer as they stood on the day the contract was drafted. Immutable (SALE-CONTRACT-001). */
export const ContractCustomerSnapshotSchema = z.strictObject({
  customerId: RecordIdSchema,
  kind: z.string().max(40),
  name: z.string().max(200),
  alternateName: z.string().max(200).optional(),
  primaryPhone: z.string().max(40),
  email: z.string().max(254).optional(),
  address: z.string().max(400).optional(),
  city: z.string().max(80).optional(),
  /** Field-restricted: absent without `crm.customer.viewIdentity`. */
  identity: ContractIdentitySnapshotSchema.optional(),
});
export type ContractCustomerSnapshot = z.infer<typeof ContractCustomerSnapshotSchema>;

/** The unit as it stood on the day the contract was drafted. Immutable. */
export const ContractUnitSnapshotSchema = z.strictObject({
  unitId: RecordIdSchema,
  code: z.string().max(80),
  projectId: RecordIdSchema,
  projectCode: z.string().max(80).optional(),
  projectName: LocalizedLabelSchema.optional(),
  buildingId: RecordIdSchema.optional(),
  buildingCode: z.string().max(80).optional(),
  floor: z.number().int().optional(),
  propertyType: z.string().max(40).optional(),
  usageType: z.string().max(40).optional(),
  finishingStatus: z.string().max(40).optional(),
  area: z.string().max(40).optional(),
  gardenArea: z.string().max(40).optional(),
  roofArea: z.string().max(40).optional(),
  bedrooms: z.number().int().optional(),
  bathrooms: z.number().int().optional(),
});
export type ContractUnitSnapshot = z.infer<typeof ContractUnitSnapshotSchema>;

/** The price as agreed. Immutable. */
export const ContractPricingSnapshotSchema = z.strictObject({
  listPrice: MoneySchema.optional(),
  agreedPrice: MoneySchema,
  discountPercentage: z.string().max(20),
  reservationAmount: MoneySchema,
  maintenanceDeposit: MoneySchema.optional(),
});
export type ContractPricingSnapshot = z.infer<typeof ContractPricingSnapshotSchema>;

/**
 * Why activation asks for a `sales.contract.exception` approval where a policy governs it. A plan
 * changed from the one the reservation was approved with is the one exception today.
 */
export const CONTRACT_EXCEPTIONS = ['planChanged'] as const;
export type ContractException = (typeof CONTRACT_EXCEPTIONS)[number];

/**
 * What the product points out but does not block — each waits on a decision (`BD-34` identity,
 * `BD-35` signed copy) before it may block anything.
 */
export const CONTRACT_WARNINGS = ['identityMissing', 'notSigned'] as const;
export type ContractWarning = (typeof CONTRACT_WARNINGS)[number];

export const SIGNING_STATES = ['unsigned', 'signed'] as const;
export const ContractSigningSchema = z.strictObject({
  state: z.enum(SIGNING_STATES),
  signedOn: BusinessDateSchema.optional(),
  /** The signed copy, a `CORE-DOC` document owned by this contract. */
  documentId: RecordIdSchema.optional(),
  recordedBy: z.string().max(200).optional(),
  recordedAt: InstantSchema.optional(),
});
export type ContractSigning = z.infer<typeof ContractSigningSchema>;

/** SALE-CHANGE-001. The new terms for what is still unpaid. */
export const AmendmentPlanSchema = z.strictObject({
  installmentCount: z.number().int().min(1).max(MAX_INSTALLMENTS),
  frequency: InstallmentFrequencySchema,
  firstDueOn: BusinessDateSchema,
  finalPayment: MoneySchema.optional(),
});
export type AmendmentPlan = z.infer<typeof AmendmentPlanSchema>;

export const AMENDMENT_STATES = ['pending', 'applied', 'rejected', 'stale'] as const;
export type AmendmentState = (typeof AMENDMENT_STATES)[number];

export const ContractAmendmentSchema = z.strictObject({
  amendmentId: RecordIdSchema,
  state: z.enum(AMENDMENT_STATES),
  reason: z.string().max(500),
  requestId: z.string().max(200).optional(),
  plan: AmendmentPlanSchema,
  /** The unpaid rows the amendment replaces; they become `rescheduled`, never deleted. */
  replacedInstallmentIds: z.array(RecordIdSchema).max(MAX_INSTALLMENTS + MAX_MILESTONES + 3),
  /** What those rows still owed — the new rows add up to exactly this. */
  amount: MoneySchema,
  rows: z.array(ScheduleRowSchema),
  requestedBy: z.string().max(200),
  requestedAt: InstantSchema,
  decidedAt: InstantSchema.optional(),
});
export type ContractAmendment = z.infer<typeof ContractAmendmentSchema>;

/* ---------------------------------------------------- contract changes (SALE-CHANGE-002/003) */

export const CONTRACT_CHANGE_KINDS = ['unitReplacement', 'assignment'] as const;
export type ContractChangeKind = (typeof CONTRACT_CHANGE_KINDS)[number];
export const CONTRACT_CHANGE_STATES = [
  'pending',
  'applied',
  'rejected',
  'stale',
  'withdrawn',
] as const;

/**
 * A change to an active contract, recorded on it as an addendum: the contract keeps its number, its
 * receipts and their allocations. A unit replacement moves the contract to another unit and
 * reschedules what is still owed against the new price; an assignment moves what is still owed to a
 * new buyer. Paid rows stay as they were — the money already received is carried, never received
 * again. Applied only after approval (`sales.contract.unitReplacement` / `.assignment`).
 */
export const ContractChangeSchema = z.strictObject({
  changeId: RecordIdSchema,
  kind: z.enum(CONTRACT_CHANGE_KINDS),
  state: z.enum(CONTRACT_CHANGE_STATES),
  reason: z.string().max(500),
  requestId: z.string().max(200).optional(),
  requestedBy: z.string().max(200),
  requestedAt: InstantSchema,
  decidedAt: InstantSchema.optional(),
  /** Why a pending change could no longer be applied (money moved meanwhile, unit taken…). */
  staleReason: z.string().max(80).optional(),
  /** What had been paid (and waived) when it was requested; a different figure at approval is stale. */
  paidAtRequest: MoneySchema,
  fromUnitId: RecordIdSchema.optional(),
  fromUnitCode: z.string().max(80).optional(),
  toUnitId: RecordIdSchema.optional(),
  toUnitCode: z.string().max(80).optional(),
  fromPrice: MoneySchema.optional(),
  toPrice: MoneySchema.optional(),
  plan: AmendmentPlanSchema.optional(),
  /** The rows it reschedules; they become `rescheduled`, never deleted. */
  replacedInstallmentIds: z.array(RecordIdSchema).max(MAX_INSTALLMENTS + MAX_MILESTONES + 3),
  /** The new rows, adding up to exactly what was still owed. */
  rows: z.array(ScheduleRowSchema),
  owed: MoneySchema,
  fromCustomerId: RecordIdSchema.optional(),
  toCustomerId: RecordIdSchema.optional(),
  fromCustomerName: z.string().max(200).optional(),
  toCustomerName: z.string().max(200).optional(),
  /** The configured fee, charged on the contract when applied (paid like any charge). */
  fee: MoneySchema.optional(),
  feeChargeId: RecordIdSchema.optional(),
});
export type ContractChange = z.infer<typeof ContractChangeSchema>;

export const ReplaceUnitSchema = z.strictObject({
  toUnitId: RecordIdSchema,
  /** How the balance on the new price is scheduled. Not needed when nothing remains owed. */
  plan: AmendmentPlanSchema.optional(),
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type ReplaceUnit = z.infer<typeof ReplaceUnitSchema>;

export const AssignContractSchema = z.strictObject({
  toCustomerId: RecordIdSchema,
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type AssignContract = z.infer<typeof AssignContractSchema>;

export const ContractChangePreviewRequestSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('unitReplacement'),
    toUnitId: RecordIdSchema,
    plan: AmendmentPlanSchema.optional(),
  }),
  z.strictObject({ kind: z.literal('assignment'), toCustomerId: RecordIdSchema }),
]);
export type ContractChangePreviewRequest = z.infer<typeof ContractChangePreviewRequestSchema>;

/** Why a change cannot be requested now; each names what to do instead. */
export const CONTRACT_CHANGE_BLOCKERS = [
  'notActive',
  'changePending',
  'amendmentPending',
  'cancellationPending',
  'notAllowedByRule',
  'ruleNotConfigured',
  'policyNotConfigured',
  'instrumentsInCustody',
  'creditOutstanding',
  'refundOpen',
  'chargesOpen',
  'unitNotAvailable',
  'sameUnit',
  'otherLegalEntity',
  'priceNotVisible',
  'paidExceedsNewPrice',
  'planRequired',
  'sameCustomer',
  'customerNotVisible',
  'minimumPaidNotReached',
  /** The unit or the new buyer is archived (ADR-0047): restore it first. */
  'unitArchived',
  'customerArchived',
] as const;
export type ContractChangeBlocker = (typeof CONTRACT_CHANGE_BLOCKERS)[number];

export const ContractChangePreviewSchema = z.strictObject({
  kind: z.enum(CONTRACT_CHANGE_KINDS),
  blockers: z.array(z.enum(CONTRACT_CHANGE_BLOCKERS)),
  /** Paid and waived on the contract's rows: carried as they are. */
  paid: MoneySchema,
  paidPercent: z.string(),
  fromPrice: MoneySchema,
  toPrice: MoneySchema.optional(),
  /** Still owed after the change, rescheduled in `rows`. */
  owed: MoneySchema,
  rows: z.array(ScheduleRowSchema),
  replacedRows: z.number().int().nonnegative(),
  fee: MoneySchema.optional(),
  feeConfigured: z.boolean(),
  minimumPaidPercent: z.string().optional(),
  toCustomerName: z.string().max(200).optional(),
  toUnitCode: z.string().max(80).optional(),
});
export type ContractChangePreview = z.infer<typeof ContractChangePreviewSchema>;

export const WithdrawContractChangeSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
});

export const ContractSchema = z.strictObject({
  contractId: RecordIdSchema,
  /** Immutable once issued, and unique. It appears on documents people keep. */
  contractNumber: BusinessCodeSchema,
  customerId: RecordIdSchema,
  unitId: RecordIdSchema,
  projectId: RecordIdSchema,
  /** Absent only on a historical contract imported at onboarding (BD-63): it had no reservation here. */
  reservationId: RecordIdSchema.optional(),
  leadId: RecordIdSchema.optional(),
  opportunityId: RecordIdSchema.optional(),
  contractedOn: BusinessDateSchema,
  /** The schedule total: the agreed price plus any maintenance deposit. */
  totalPrice: MoneySchema,
  /** The deposit agreed on the reservation. An agreement, not money: see `reservationFundsApplied`. */
  reservationAmount: MoneySchema,
  /**
   * The reservation's effective deposit receipts allocated to the schedule at activation — the only
   * reservation money a schedule is credited with. Absent on a contract activated before 2026-10-21,
   * whose schedule was credited with the agreed `reservationAmount` without a receipt behind it.
   */
  reservationFundsApplied: MoneySchema.optional(),
  /**
   * What happened to the unit when the contract was cancelled: returned to sale, or kept (still
   * `contracted`, linked to this cancelled contract) until a person releases it with a reason.
   */
  unitDisposition: z.enum(['released', 'retained']).optional(),
  unitRelease: z
    .strictObject({
      releasedAt: InstantSchema,
      releasedByAccountId: z.string(),
      reason: z.string(),
    })
    .optional(),
  /** The refund obligation a cancellation created (collections), when money had been received. */
  refundId: RecordIdSchema.optional(),
  /**
   * The plan the schedule was generated from. Absent on a historical contract (BD-63): its schedule
   * was imported row by row from the client's records, and no plan generates it.
   */
  paymentPlan: PaymentPlanSchema.optional(),
  /** Set when the contract was onboarded from the client's records rather than made here (BD-63). */
  origin: HistoricalOriginSchema.optional(),
  /** Sum of every unpaid installment. Maintained by the collections module. */
  outstandingAmount: MoneySchema,
  paidAmount: MoneySchema,
  /** Snapshots taken when the draft was made (SALE-CONTRACT-001); absent on pre-BMP-1 contracts. */
  customerSnapshot: ContractCustomerSnapshotSchema.optional(),
  unitSnapshot: ContractUnitSnapshotSchema.optional(),
  pricing: ContractPricingSnapshotSchema.optional(),
  parties: z.array(ContractPartySchema).max(MAX_CONTRACT_PARTIES),
  signing: ContractSigningSchema,
  exceptions: z.array(z.enum(CONTRACT_EXCEPTIONS)),
  /** Computed on read; never stored. */
  warnings: z.array(z.enum(CONTRACT_WARNINGS)),
  approvals: z.array(
    z.strictObject({ operationType: z.string(), requestId: z.string().min(1).max(200) }),
  ),
  amendments: z.array(ContractAmendmentSchema),
  /** Unit replacements and assignments (SALE-CHANGE-002/003), oldest first. */
  changes: z.array(ContractChangeSchema),
  pendingCancellation: z.strictObject({ requestId: z.string(), reason: z.string() }).optional(),
  /** Set when the contract ends with money taken on it; BMP-2 settles it. */
  refundHandoff: z.enum(REFUND_HANDOFF_STATES),
  /** The money side of a cancellation after collection (SALE-CANCEL-002). */
  settlement: z
    .strictObject({
      collected: MoneySchema,
      penalty: MoneySchema,
      refundable: MoneySchema,
      penaltySource: z.enum(['configured', 'entered']),
      refundId: z.string().optional(),
    })
    .optional(),
  /** The rows a draft would freeze — computed from the plan on read, for the preview. */
  draftSchedule: z.array(ScheduleRowSchema).optional(),
  salesOwnerAccountId: z.string().min(1).max(200),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  departmentId: RecordIdSchema.optional(),
  teamId: RecordIdSchema.optional(),
  state: ContractStateSchema,
  activatedAt: InstantSchema.optional(),
  cancellationReason: z.string().max(500).optional(),
  /** Placeholder for the signed document once `CORE-DOC` exists. Never a file, never a URL yet. */
  documentRef: z.string().max(200).optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Contract = z.infer<typeof ContractSchema>;

export const CreateContractSchema = z.strictObject({
  reservationId: RecordIdSchema,
  contractedOn: BusinessDateSchema,
  /** Omitted keeps the reservation's plan. Supplying one replaces it wholesale. */
  paymentPlan: PaymentPlanSchema.optional(),
  /** Omitted: the reservation's customer as the only buyer, owning 100 %. */
  parties: z.array(ContractPartyInputSchema).min(1).max(MAX_CONTRACT_PARTIES).optional(),
  idempotencyKey: z.string().min(8).max(200),
});
export type CreateContract = z.infer<typeof CreateContractSchema>;

export const SetContractPartiesSchema = z.strictObject({
  parties: z.array(ContractPartyInputSchema).min(1).max(MAX_CONTRACT_PARTIES),
  expectedVersion: z.number().int().positive(),
});
export type SetContractParties = z.infer<typeof SetContractPartiesSchema>;

export const ActivateContractSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
  /**
   * Optional idempotency key. A retried activation carrying the key that moved the draft on answers
   * with the contract as it now stands — active, or waiting for approval — instead of a stale-version
   * conflict. Activation itself happens once either way: the version is checked in the update.
   */
  idempotencyKey: z.string().min(8).max(200).optional(),
});
export type ActivateContract = z.infer<typeof ActivateContractSchema>;

/**
 * SALE-CONTRACT-001, COL-SCHEDULE-001. Replace a draft's payment plan. Draft only; the price is the
 * snapshot's and does not change. A plan that differs from the reservation's carries the
 * `planChanged` exception, which activation submits for approval where a policy governs it.
 */
export const SetContractPlanSchema = z.strictObject({
  paymentPlan: PaymentPlanSchema,
  expectedVersion: z.number().int().positive(),
});
export type SetContractPlan = z.infer<typeof SetContractPlanSchema>;

/** Why a draft cannot be activated right now, before anyone presses the button. */
export const ACTIVATION_BLOCKERS = [
  'notDraft',
  'reservationNotConfirmed',
  'approvalPending',
  'notPermitted',
  /** `sales.depositRequiredBeforeActivation` asks for the agreed deposit and less was received. */
  'depositNotReceived',
  /** More reservation money was received than the contract's schedule can take. */
  'fundsExceedContract',
] as const;

/**
 * What activating a draft would do, asked before doing it (SALE-CONTRACT-003): whether the draft
 * carries an exception, whether a published policy would send it for approval, what stops it, and
 * the rows it would freeze. Reads only; changes nothing.
 */
export const ActivationReviewSchema = z.strictObject({
  contractId: RecordIdSchema,
  state: ContractStateSchema,
  version: z.number().int().positive(),
  exceptions: z.array(z.enum(CONTRACT_EXCEPTIONS)),
  /** True when activation would wait for an approval instead of activating at once. */
  approvalRequired: z.boolean(),
  blockers: z.array(z.enum(ACTIVATION_BLOCKERS)),
  warnings: z.array(z.enum(CONTRACT_WARNINGS)),
  /** The rows activation would turn into instalments, with the reservation money credited. */
  rows: z.array(ScheduleRowSchema),
  total: MoneySchema,
  /** The effective deposit receipts activation would allocate — never the agreed amount alone. */
  reservationCredit: MoneySchema,
  /** The deposit agreed on the reservation, beside what was actually received. */
  agreedDeposit: MoneySchema,
  /** `sales.depositRequiredBeforeActivation` (null: not configured — nothing required). */
  depositRule: z.enum(['notRequired', 'agreedDepositReceived']).nullable(),
  /** What the reservation was approved with — shown beside the draft's plan when they differ. */
  reservationPlan: PaymentPlanSchema.optional(),
});
export type ActivationReview = z.infer<typeof ActivationReviewSchema>;

export const RecordSigningSchema = z.strictObject({
  signedOn: BusinessDateSchema,
  documentId: RecordIdSchema.optional(),
  expectedVersion: z.number().int().positive(),
});
export type RecordSigning = z.infer<typeof RecordSigningSchema>;

export const AmendContractSchema = z.strictObject({
  plan: AmendmentPlanSchema,
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type AmendContract = z.infer<typeof AmendContractSchema>;

export const CancelContractSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  /** Return the unit to the market. Refused when money has already been collected. */
  releaseUnit: z.boolean().default(true),
});
export type CancelContract = z.infer<typeof CancelContractSchema>;

/** Return to sale a unit kept after its contract was cancelled. */
export const ReleaseRetainedUnitSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});
export type ReleaseRetainedUnit = z.infer<typeof ReleaseRetainedUnitSchema>;

/**
 * Cancel an active contract on which money was collected (SALE-CANCEL-002). The penalty comes from
 * `sales.cancellationPenalty` when configured (`BD-05`); otherwise the person enters it and an
 * approval policy must govern the cancellation, or it is refused. What remains is handed to
 * collections as a refund request — never paid here.
 */
export const SettleCancellationSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  /** Required — and only accepted — when no penalty rule is configured. */
  penalty: DecimalStringSchema.optional(),
  releaseUnit: z.boolean().default(true),
  expectedVersion: z.number().int().positive(),
});
export type SettleCancellation = z.infer<typeof SettleCancellationSchema>;

export const SettlementPreviewSchema = z.strictObject({
  collected: MoneySchema,
  /** `null` when no rule is configured: the person enters the penalty. */
  penalty: MoneySchema.nullable(),
  penaltySource: z.enum(['configured', 'entered']),
  refundable: MoneySchema.nullable(),
  approvalRequired: z.boolean(),
  /** Reasons the cancellation cannot proceed now, e.g. instruments still in custody. */
  blockers: z.array(z.string()),
  decision: z.literal('BD-05'),
});
export type SettlementPreview = z.infer<typeof SettlementPreviewSchema>;

export const ContractQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
  state: ContractStateSchema.optional(),
  projectId: RecordIdSchema.optional(),
  customerId: RecordIdSchema.optional(),
  salesOwnerAccountId: z.string().min(1).max(200).optional(),
  /** Contract date inside these days, both inclusive (the dashboard's period drill-down). */
  contractedFrom: BusinessDateSchema.optional(),
  contractedTo: BusinessDateSchema.optional(),
});
export type ContractQuery = z.infer<typeof ContractQuerySchema>;

export const ContractPageSchema = z.strictObject({
  items: z.array(ContractSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type ContractPage = z.infer<typeof ContractPageSchema>;

/**
 * SALE-CONTRACT-004. The contract's own trail, read from the audit record: what happened, when, by
 * whom. Change details stay behind the audit permissions; this is the summary a contract viewer sees.
 */
export const ContractHistoryEntrySchema = z.strictObject({
  action: z.string(),
  outcome: z.string(),
  occurredAt: InstantSchema,
  actorAccountId: z.string().optional(),
  actorKind: z.string(),
  reason: z.string().optional(),
});
export type ContractHistoryEntry = z.infer<typeof ContractHistoryEntrySchema>;
export const ContractHistorySchema = z.strictObject({
  items: z.array(ContractHistoryEntrySchema),
});

/* ------------------------------------------------------------------- quotation */

/**
 * SALE-QUOTE-001. A priced offer for one unit and one plan. It **never reserves inventory**: the unit
 * stays on sale and anyone may reserve it. A revision is a new record with the next revision number;
 * the one it replaces becomes `superseded`. `expired` is computed from the validity the person stated
 * (`BD-36` decides nothing yet) and never stored.
 */
export const QUOTATION_STATES = ['active', 'superseded', 'withdrawn', 'expired'] as const;
export const QuotationStateSchema = z.enum(QUOTATION_STATES);
export type QuotationState = z.infer<typeof QuotationStateSchema>;

export const QuotationSchema = z.strictObject({
  quotationId: RecordIdSchema,
  quotationNumber: BusinessCodeSchema,
  revision: z.number().int().positive(),
  customerId: RecordIdSchema.optional(),
  leadId: RecordIdSchema.optional(),
  opportunityId: RecordIdSchema.optional(),
  unitId: RecordIdSchema,
  unitCode: z.string().max(80),
  projectId: RecordIdSchema,
  /** The unit's effective price when the quotation was made. */
  listPrice: MoneySchema,
  agreedPrice: MoneySchema,
  discountPercentage: z.string().max(20),
  paymentPlan: PaymentPlanSchema,
  rows: z.array(ScheduleRowSchema),
  total: MoneySchema,
  validUntil: BusinessDateSchema,
  state: QuotationStateSchema,
  withdrawalReason: z.string().max(500).optional(),
  notes: NoteSchema.optional(),
  salesOwnerAccountId: z.string().min(1).max(200),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  departmentId: RecordIdSchema.optional(),
  teamId: RecordIdSchema.optional(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Quotation = z.infer<typeof QuotationSchema>;

export const CreateQuotationSchema = z
  .strictObject({
    customerId: RecordIdSchema.optional(),
    leadId: RecordIdSchema.optional(),
    opportunityId: RecordIdSchema.optional(),
    unitId: RecordIdSchema,
    agreedPrice: MoneySchema,
    paymentPlan: PaymentPlanSchema,
    validUntil: BusinessDateSchema,
    notes: NoteSchema.optional(),
    idempotencyKey: z.string().min(8).max(200),
  })
  .refine((value) => value.customerId !== undefined || value.leadId !== undefined, {
    message: 'QUOTATION_NEEDS_RECIPIENT',
    path: ['customerId'],
  });
export type CreateQuotation = z.infer<typeof CreateQuotationSchema>;

export const ReviseQuotationSchema = z.strictObject({
  agreedPrice: MoneySchema,
  paymentPlan: PaymentPlanSchema,
  validUntil: BusinessDateSchema,
  notes: NoteSchema.optional(),
  /** The revision being replaced; a stale one is refused. */
  expectedRevision: z.number().int().positive(),
});
export type ReviseQuotation = z.infer<typeof ReviseQuotationSchema>;

export const WithdrawQuotationSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  expectedRevision: z.number().int().positive(),
});
export type WithdrawQuotation = z.infer<typeof WithdrawQuotationSchema>;

export const QuotationQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
  customerId: RecordIdSchema.optional(),
  leadId: RecordIdSchema.optional(),
  opportunityId: RecordIdSchema.optional(),
  unitId: RecordIdSchema.optional(),
  /**
   * The state as it reads: `active` is still within its validity, `expired` is active past it — the
   * same rule a single read applies, evaluated by the database against the organization's today.
   */
  state: z.enum(['active', 'withdrawn', 'expired']).optional(),
  /** Anchored prefix of the quotation number or the unit code. Never a regular expression. */
  search: z.string().trim().min(1).max(40).optional(),
});
export type QuotationQuery = z.infer<typeof QuotationQuerySchema>;

/**
 * The commercial defaults a salesperson needs to see while working — never the whole settings
 * catalog, which is administrative. `null` is **not configured** and names the decision it waits on.
 */
export const SalesDefaultsSchema = z.strictObject({
  reservationValidityDays: z.number().int().positive().nullable(),
  quotationValidityDays: z.number().int().positive().nullable(),
  decisions: z.strictObject({
    reservationValidityDays: z.string(),
    quotationValidityDays: z.string(),
  }),
});
export type SalesDefaults = z.infer<typeof SalesDefaultsSchema>;

export const QuotationPageSchema = z.strictObject({
  items: z.array(QuotationSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type QuotationPage = z.infer<typeof QuotationPageSchema>;

export const QuotationRevisionsSchema = z.strictObject({ items: z.array(QuotationSchema) });

/* ----------------------------------------------------------------- installment */

export const INSTALLMENT_STATES = [
  'upcoming',
  'due',
  'partiallyPaid',
  'paid',
  'overdue',
  'rescheduled',
  'cancelled',
  /** Settled entirely by an approved waiver, nothing paid (COL-RECEIPT-004). */
  'waived',
] as const;
export const InstallmentStateSchema = z.enum(INSTALLMENT_STATES);
export type InstallmentState = z.infer<typeof InstallmentStateSchema>;

export const InstallmentSchema = z.strictObject({
  installmentId: RecordIdSchema,
  contractId: RecordIdSchema,
  customerId: RecordIdSchema,
  unitId: RecordIdSchema,
  projectId: RecordIdSchema,
  sequence: z.number().int().positive(),
  kind: InstallmentKindSchema,
  dueOn: BusinessDateSchema,
  amount: MoneySchema,
  paidAmount: MoneySchema,
  /** Settled by approved waivers; the contractual `amount` never changes (COL-RECEIPT-004). */
  waivedAmount: MoneySchema.optional(),
  remainingAmount: MoneySchema,
  state: InstallmentStateSchema,
  /** A milestone's wording. */
  label: LocalizedLabelSchema.optional(),
  /** The amendment that created this row (SALE-CHANGE-001). */
  amendmentId: RecordIdSchema.optional(),
  /** The unit replacement or assignment that created this row (SALE-CHANGE-002/003). */
  changeId: RecordIdSchema.optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  teamId: RecordIdSchema.optional(),
  salesOwnerAccountId: z.string().min(1).max(200),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Installment = z.infer<typeof InstallmentSchema>;

export const InstallmentQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().min(1).max(200).optional(),
  contractId: RecordIdSchema.optional(),
  customerId: RecordIdSchema.optional(),
  projectId: RecordIdSchema.optional(),
  state: InstallmentStateSchema.optional(),
  /** `due` is on or before today; `overdue` is strictly before; `upcoming` is a forward window. */
  bucket: z.enum(['due', 'overdue', 'upcoming']).optional(),
  /** Days ahead for the `upcoming` bucket. The reminder window is 15 days. */
  withinDays: z.coerce.number().int().min(1).max(365).optional(),
  /** Due inside these days, both inclusive (the dashboard's period drill-down). */
  dueFrom: BusinessDateSchema.optional(),
  dueTo: BusinessDateSchema.optional(),
});
export type InstallmentQuery = z.infer<typeof InstallmentQuerySchema>;

export const InstallmentPageSchema = z.strictObject({
  items: z.array(InstallmentSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type InstallmentPage = z.infer<typeof InstallmentPageSchema>;

/** What a schedule preview returns, before anything is stored. */
export const SchedulePreviewSchema = z.strictObject({
  rows: z.array(ScheduleRowSchema),
  /** The schedule total: the price plus any maintenance deposit. */
  total: MoneySchema,
  price: MoneySchema,
  maintenanceDeposit: MoneySchema.optional(),
  /** Stated, not implied (`BD-32`). */
  roundingRule: z.literal(SCHEDULE_ROUNDING_RULE),
  /** Present so a caller can see the reconciliation rather than trust it. */
  rowsTotal: MoneySchema,
});
export type SchedulePreview = z.infer<typeof SchedulePreviewSchema>;

export const PreviewScheduleSchema = z.strictObject({
  total: MoneySchema,
  paymentPlan: PaymentPlanSchema,
});

export const InstallmentListSchema = z.strictObject({ items: z.array(InstallmentSchema) });

/**
 * The contract portfolio the actor can see, totalled on the server (SEC-028, ADR-0007).
 *
 * Totals are computed by the database over **every** contract inside the actor's scope, in
 * `Decimal128`, and grouped by currency — never by a client adding up one page of rows, which both
 * truncates at the page size and passes money through binary floating point. Two currencies are
 * never added together, so each appears as its own row.
 */
export const ContractSummaryQuerySchema = z.strictObject({
  state: ContractStateSchema.optional(),
  projectId: RecordIdSchema.optional(),
  /** Contracts dated within a period, inclusive (the sales report's period, 2026-10-23). */
  contractedFrom: BusinessDateSchema.optional(),
  contractedTo: BusinessDateSchema.optional(),
});
export type ContractSummaryQuery = z.infer<typeof ContractSummaryQuerySchema>;

export const ContractSummarySchema = z.strictObject({
  contracts: z.number().int().nonnegative(),
  byCurrency: z.array(
    z.strictObject({
      currency: CurrencyCodeSchema,
      contracts: z.number().int().nonnegative(),
      totalContracted: MoneySchema,
      totalPaid: MoneySchema,
      totalOutstanding: MoneySchema,
    }),
  ),
});
export type ContractSummary = z.infer<typeof ContractSummarySchema>;

/**
 * The company dashboard's sales and collections figures, computed on the server over every record in
 * the actor's scope, per currency (never added across currencies), in exact decimals.
 *
 * Definitions (docs/architecture/sales-money.md):
 * - `contractedValue`: schedule totals of contracts that are `active` or `completed` — a completed
 *   contract stays in it; drafts, contracts waiting on approval and cancelled contracts do not count.
 * - `installmentCredit`: what those contracts' instalments were settled with — receipts allocated to
 *   them, reservation deposits applied at activation, and historical receipts (BD-63).
 * - `outstanding`: what `active` contracts still owe.
 * - `effectiveReceipts`: money received as new receipts (posted, not reversed, not historical),
 *   including reservation deposits not yet applied, whatever the contract's later state.
 * - `historicalReceipts`: receipts onboarded from the client's records — inside opening balances.
 * - `unappliedReservationFunds`: deposits received on reservations and not yet allocated.
 * - `collectionRatePercent`: installmentCredit ÷ contractedValue × 100, two decimals, `null` when
 *   nothing is contracted.
 * - `overdue`: instalments of active contracts past due, counted and summed by the database.
 * Treasury cash, recognised revenue and profit are separate figures with their own reports.
 */
export const PortfolioMetricsQuerySchema = z
  .strictObject({
    projectId: RecordIdSchema.optional(),
    legalEntityId: RecordIdSchema.optional(),
    branchId: RecordIdSchema.optional(),
    /**
     * A period, both ends inclusive, in the organization's calendar. The position figures stay "as of
     * today" whatever the period; the period adds the activity that happened inside it.
     */
    from: BusinessDateSchema.optional(),
    to: BusinessDateSchema.optional(),
  })
  .refine((value) => (value.from === undefined) === (value.to === undefined), {
    message: 'PERIOD_NEEDS_BOTH_ENDS',
    path: ['to'],
  })
  .refine((value) => !value.from || !value.to || value.from <= value.to, {
    message: 'DATE_RANGE_INVALID',
    path: ['to'],
  });
export type PortfolioMetricsQuery = z.infer<typeof PortfolioMetricsQuerySchema>;

/** Days past due: 1–30, 31–60, 61–90, over 90. */
export const OVERDUE_AGEING_BUCKETS = ['days1to30', 'days31to60', 'days61to90', 'over90'] as const;

/**
 * The company dashboard's portfolio. The collection figures — credited, received, outstanding,
 * overdue — are present only for a reader holding a collections permission (2026-10-26, access
 * matrix): a sales reader gets contracts and their value, never the collections ledger.
 */
export const PortfolioMetricsSchema = z.strictObject({
  asOf: BusinessDateSchema,
  activeContracts: z.number().int().nonnegative(),
  completedContracts: z.number().int().nonnegative(),
  cancelledContracts: z.number().int().nonnegative(),
  byCurrency: z.array(
    z.strictObject({
      currency: CurrencyCodeSchema,
      contractedValue: MoneySchema,
      installmentCredit: MoneySchema.optional(),
      outstanding: MoneySchema.optional(),
      effectiveReceipts: MoneySchema.optional(),
      historicalReceipts: MoneySchema.optional(),
      unappliedReservationFunds: MoneySchema.optional(),
      collectionRatePercent: z.string().nullable().optional(),
      overdue: z
        .strictObject({
          count: z.number().int().nonnegative(),
          amount: MoneySchema,
          ageing: z.array(
            z.strictObject({
              bucket: z.enum(OVERDUE_AGEING_BUCKETS),
              count: z.number().int().nonnegative(),
              amount: MoneySchema,
            }),
          ),
        })
        .optional(),
    }),
  ),
  /**
   * Activity inside the requested period (FIN-REPORT-016), with explicit meanings: contracts by their
   * contract date (activated ones), receipts by the day the money was received (new money only, not
   * historical, not reversed), instalments by their due date with what was paid or waived on them,
   * and reservations by their reservation date.
   */
  period: z
    .strictObject({
      from: BusinessDateSchema,
      to: BusinessDateSchema,
      contractsSigned: z.number().int().nonnegative(),
      contractsCancelled: z.number().int().nonnegative(),
      reservationsMade: z.number().int().nonnegative(),
      byCurrency: z.array(
        z.strictObject({
          currency: CurrencyCodeSchema,
          signedValue: MoneySchema,
          receipts: MoneySchema.optional(),
          dueInPeriod: MoneySchema.optional(),
          settledOfDue: MoneySchema.optional(),
          collectionRatePercent: z.string().nullable().optional(),
        }),
      ),
    })
    .optional(),
});
export type PortfolioMetrics = z.infer<typeof PortfolioMetricsSchema>;

/** Who may read collection figures in the portfolio: a collections permission, nothing less. */
export const COLLECTION_FIGURE_PERMISSIONS = [
  'collection.report.view',
  'collection.installment.view',
] as const;

/** The portfolio without its collection figures, for a reader who may not see them. */
export function withoutCollectionFigures(metrics: PortfolioMetrics): PortfolioMetrics {
  return {
    ...metrics,
    byCurrency: metrics.byCurrency.map((row) => ({
      currency: row.currency,
      contractedValue: row.contractedValue,
    })),
    ...(metrics.period
      ? {
          period: {
            ...metrics.period,
            byCurrency: metrics.period.byCurrency.map((row) => ({
              currency: row.currency,
              signedValue: row.signedValue,
            })),
          },
        }
      : {}),
  };
}

/**
 * The portfolio per project and currency (2026-10-23, dashboard): what was contracted (active and
 * completed contracts), what was credited against their instalments, and what active contracts still
 * owe — the same definitions as the portfolio metrics, summed by the database over the whole scope.
 * One row per project and currency; two currencies are never added together.
 */
export const PortfolioByProjectSchema = z.strictObject({
  items: z.array(
    z.strictObject({
      projectId: RecordIdSchema,
      currency: z.string().regex(/^[A-Z]{3}$/),
      contracts: z.number().int().nonnegative(),
      contractedValue: MoneySchema,
      /** Collection figures: present only for a collections reader, as above. */
      installmentCredit: MoneySchema.optional(),
      outstanding: MoneySchema.optional(),
    }),
  ),
});
export type PortfolioByProject = z.infer<typeof PortfolioByProjectSchema>;

/** A customer's financial position across all their contracts, within the actor's scope. */
export const CustomerFinancialSummarySchema = z.strictObject({
  customerId: RecordIdSchema,
  contracts: z.number().int().nonnegative(),
  /** Of `contracts`: active and completed (2026-10-22). */
  activeContracts: z.number().int().nonnegative(),
  completedContracts: z.number().int().nonnegative(),
  /** Reservations still holding a unit for this customer. */
  liveReservations: z.number().int().nonnegative(),
  totalContracted: MoneySchema,
  totalPaid: MoneySchema,
  totalOutstanding: MoneySchema,
  overdueCount: z.number().int().nonnegative(),
  overdueAmount: MoneySchema,
});
export type CustomerFinancialSummary = z.infer<typeof CustomerFinancialSummarySchema>;

export const SALES_AUDIT_ACTIONS = {
  reservationCreated: 'sales.reservation.created',
  reservationConfirmed: 'sales.reservation.confirmed',
  reservationCancelled: 'sales.reservation.cancelled',
  reservationExpired: 'sales.reservation.expired',
  reservationRefused: 'sales.reservation.refused',
  reservationApprovalRequested: 'sales.reservation.approvalRequested',
  reservationApproved: 'sales.reservation.approved',
  reservationRejected: 'sales.reservation.rejected',
  reservationExtended: 'sales.reservation.extended',
  reservationUpdated: 'sales.reservation.updated',
  reservationExtensionRequested: 'sales.reservation.extensionRequested',
  reservationCancellationRequested: 'sales.reservation.cancellationRequested',
  contractCreated: 'sales.contract.created',
  contractActivated: 'sales.contract.activated',
  /** A historical contract onboarded active with its imported schedule (BD-63). */
  contractOnboarded: 'sales.contract.onboarded',
  contractPlanChanged: 'sales.contract.planChanged',
  contractActivationRequested: 'sales.contract.activationRequested',
  contractActivationRejected: 'sales.contract.activationRejected',
  contractPartiesChanged: 'sales.contract.partiesChanged',
  contractSigned: 'sales.contract.signed',
  contractAmendmentRequested: 'sales.contract.amendmentRequested',
  contractAmended: 'sales.contract.amended',
  contractChangeRequested: 'sales.contract.changeRequested',
  contractChangeApplied: 'sales.contract.changeApplied',
  contractChangeDeclined: 'sales.contract.changeDeclined',
  contractAmendmentRejected: 'sales.contract.amendmentRejected',
  contractCancellationRequested: 'sales.contract.cancellationRequested',
  contractCancelled: 'sales.contract.cancelled',
  /** A unit kept after its contract's cancellation returned to sale by a person, with a reason. */
  contractUnitReleased: 'sales.contract.unitReleased',
  quotationCreated: 'sales.quotation.created',
  quotationRevised: 'sales.quotation.revised',
  quotationWithdrawn: 'sales.quotation.withdrawn',
  contractRefused: 'sales.contract.refused',
  scheduleGenerated: 'sales.schedule.generated',
  installmentsRefreshed: 'sales.installments.refreshed',
} as const;

/** Convenience for a caller that needs the plan's last due date, e.g. to bound a reminder sweep. */
export function lastDueDate(plan: PaymentPlan): BusinessDate {
  const step = MONTHS_PER_FREQUENCY[plan.frequency];
  const cycles = Math.max(plan.installmentCount - 1, 0) + (plan.finalPayment ? 1 : 0);
  const periodic =
    plan.installmentCount === 0 && !plan.finalPayment
      ? (plan.downPaymentDueOn ?? plan.firstDueOn)
      : addMonths(plan.firstDueOn, step * cycles);
  // Milestones and a maintenance deposit may fall after the periodic rows.
  return [
    ...(plan.milestones ?? []).map((milestone) => milestone.dueOn),
    ...(plan.maintenanceDeposit ? [plan.maintenanceDeposit.dueOn] : []),
  ].reduce<BusinessDate>((latest, date) => (date > latest ? date : latest), periodic);
}
